import React, { useState, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useAuthInfo } from '@propelauth/react'

import {
    Box,
    Typography,
    Alert,
    Modal,
    IconButton,
    Button,
} from '@mui/material'

import { BackIconButton, ContinueButton } from '../../../../components/Buttons'
import SelectAllCheckbox from '../../../../components/SelectAllCheckbox'
import { SectionCard, SectionAccordion } from '../../../../components/sections'
import { useDocumentCreation } from '../../../../contexts/DocumentCreationContext'
import { 
    transformFlatSectionsToTree, 
    getTopLevelSections, 
    getChildSections, 
    getAllDescendants, 
    DocumentTypeConfigs 
} from '../../../../utils/documentTreeUtils'
import { placeholder_regexps } from '../../../../utils/Regexps/placeholder_regexps'
import { getOneMRTTemplate } from '../../../../utils/utils'
import GradientCircularProgressIndicator from '../../../../components/GradientCircularProgressIndicator'
import CloseIcon from '@mui/icons-material/Close'
import { sectionSelectionStorage } from '../../../../services/SectionSelectionStorage'

const SelectDocumentSectionToBeGenerated = ({ 
    onNextClick,
    onBackClick,
    documentName,
    productName,
    templateType,
    setSteps,
    steps,
}) => {
    const auth = useAuthInfo()
    const { document, updateDocument, setAllSections, allSections } = useDocumentCreation()
    const [sections, setSections] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const [expandedAccordions, setExpandedAccordions] = useState({})

    useEffect(() => {
        async function getOutlineContent() {
            setLoading(true)
            try {
                console.log("Document", document.templateType)
                // Transform flat sections into hierarchical tree structure
                const org = auth.orgHelper.getOrgs()[0].orgId
                const orgAccessToken = await auth.tokens.getAccessTokenForOrg(org)
                console.log("Org Access Token", orgAccessToken)
                
                const mrt_template = await getOneMRTTemplate(document.templateType.template_id, orgAccessToken)
                console.log("MRT Template", mrt_template)

                const rawSections = mrt_template.sections
                const flatSections = rawSections || []
                
                console.log("Flat Sections", flatSections)
                
                // Use the regulatory document configuration for pharmaceutical/regulatory documents
                const transformSections = transformFlatSectionsToTree(
                    flatSections,
                    DocumentTypeConfigs.REGULATORY
                )

                console.log("Transform Sections", transformSections)

                // setSections(transformSections)
                setSections(flatSections)
                console.log("flatSections", flatSections)

                // Initialize all sections as selected by default
                // const allSections = transformSections.map(section => section)
                const allSectionIds = flatSections.map(section => section.section_id)
                console.log("flatSections", flatSections)
                setAllSections(flatSections)
                console.log('allSectionIds', allSectionIds)
                updateDocument({
                    selectedSections: allSectionIds
                })
            } catch (error) {
                console.error("Error getting outline content", error)
                setError(true)
            } finally {
                setLoading(false)
            }
        }
        if (allSections.length === 0) {
            getOutlineContent()
        } else {
            setSections(allSections)
        }
    }, [document.templateType])

    // Helper functions for section organization
    const getTopLevelSectionsLocal = () => {
        return getTopLevelSections(sections)
    }

    const getChildSectionsLocal = (parentSection) => {
        return getChildSections(parentSection, sections)
    }

    // Helper functions for selection state
    const getSelectAllState = () => {
        const selectedCount = document.selectedSections?.length || 0
        const totalCount = sections.length
        
        if (selectedCount === 0) {
            return { allSelected: false, noneSelected: true, someSelected: false }
        } else if (selectedCount === totalCount) {
            return { allSelected: true, noneSelected: false, someSelected: false }
        } else {
            return { allSelected: false, noneSelected: false, someSelected: true }
        }
    }

    const getAllDescendantsLocal = (parentSection) => {
        return getAllDescendants(parentSection, sections)
    }

    const handleSectionToggle = (section) => {
        const currentSelected = document.selectedSections || []
        console.log(section)
        const isCurrentlySelected = currentSelected.includes(section.section_id)
        
        let newSelected = [...currentSelected]
        
        if (isCurrentlySelected) {
            // Deselect the section and all its descendants
            newSelected = newSelected.filter(selected => selected !== section.section_id)

            const descendants = getAllDescendantsLocal(section)
            const descendantIds = descendants.map(d => d.section_id)
            
            newSelected = newSelected.filter(selected => !descendantIds.includes(selected))
        } else {
            // Select the section and all its descendants
            if (!newSelected.includes(section.section_id)) {
                newSelected.push(section.section_id)
            }
            const descendants = getAllDescendantsLocal(section)
            descendants.forEach(descendant => {
                if (!newSelected.includes(descendant.section_id)) {
                    newSelected.push(descendant.section_id)
                }
            })
        }
        
        updateDocument({ selectedSections: newSelected })
        console.log('selectedSections', document.selectedSections)
    }

    const handleSelectAllToggle = () => {
        const { allSelected } = getSelectAllState()
        
        if (allSelected) {
            // Deselect all
            updateDocument({ selectedSections: [] })
            console.log('selectedSections', document.selectedSections)
        } else {
            // Select all
            const allSectionIds = sections.map(section => section.section_id)
            updateDocument({ selectedSections: allSectionIds })
            console.log('allSections', allSectionIds)
        }
    }

    const handleAccordionToggle = (sectionName) => {
        setExpandedAccordions(prev => ({
            ...prev,
            [sectionName]: !prev[sectionName]
        }))
    }

    // const checkIfSectionHasQueryVariables = () => {
    //     const sectionsWithQueryVariables = []
    //     document.selectedSections.map((section) => {
    //         console.log('section', section) // TODO: remove this
    //         if (section.fixed_elements) {
    //             section.fixed_elements.map((element) => {
    //                 let query_sql = element.query.query_sql
    //                 if (query_sql) {
    //                     for (const regex of placeholder_regexps) {
    //                         if (query_sql.match(regex)) {
    //                             sectionsWithQueryVariables.push(section)
    //                             break
    //                         }
    //                     }
    //                 }
    //             })
    //         }
    //     })
    //     document.sectionsWithQueryVariables = sectionsWithQueryVariables
    //     return document.sectionsWithQueryVariables
    // }

    const handleContinue = () => {
        // const sectionsWithQueryVariables = checkIfSectionHasQueryVariables()
        // console.log('sectionsWithQueryVariables', sectionsWithQueryVariables)
        // if (sectionsWithQueryVariables) {
        //     const newSteps = [...steps]
        //     newSteps.splice(2, 0, 'Database Query')
        //     setSteps(newSteps)
        //     console.log('steps', steps)
        //     onNextClick()
        // } else {
        //     onNextClick()
        // }
        console.log('document.selectedSections', document.selectedSections)
        // selectedSections already contains section_ids, no need to map
        onNextClick()
    }

    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '700px',
                width: '100%' 
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '16px',
                    height: '100%'
                }}>
                    <GradientCircularProgressIndicator size={100} />
                    <Typography
                        sx={{
                            fontSize: '24px',
                            fontWeight: '600',
                            color: '#374151'
                        }}
                    >
                        Loading sections...
                    </Typography>
                </Box>
            </Box>
        )
    }

    if (error) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '400px',
                width: '100%' 
            }}>
                <Alert severity="error">
                    Error loading sections. Please try again.
                </Alert>
            </Box>
        )
    }

    // Get top level sections to determin how many cards/accordions are needed
    const topLevelSections = getTopLevelSectionsLocal()
    // Get the select all state to determine the select all checkbox state
    const selectAllState = getSelectAllState()

    return (
        <Box sx={{ 
            width: '100%', 
            height: '100%', 
            padding: '40px',
            paddingTop: '120px', // Account for fixed stepper
            '@media (max-width: 768px)': {
                paddingTop: '100px',
            },
            position: 'relative'
        }}>
            <Box sx={{
                position: 'fixed',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 999,
                width: 'clamp(600px, 60vw, 900px)',
                '@media (max-width: 768px)': {
                    width: 'calc(100vw - 32px)',
                    left: '16px',
                    transform: 'none',
                },
                overflowY: 'auto',
                maxHeight: 'calc(100vh - 180px)',
                paddingBottom: '120px',
                // Styled scrollbar
                scrollbarWidth: 'thin', // Firefox
                scrollbarColor: '#888 #f1f1f1', // Firefox
                '&::-webkit-scrollbar': {
                    width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: '#888',
                    borderRadius: '4px',
                    '&:hover': {
                        background: '#555',
                    }
                }
            }}>
                <Box sx={{ 
                    position: 'sticky', 
                    top: 0,
                    backgroundColor: 'white', 
                    zIndex: 999, 
                    background: '#F9FAFB' 
                }}>
                    {/* Header */}
                    <Box sx={{ mb: '24px' }}>
                        <BackIconButton onClick={onBackClick} />
                        <Box>
                            <Typography 
                                variant="h4" 
                                sx={{ 
                                    fontSize: '24px',
                                    fontWeight: 700,
                                    color: '#374151',
                                    fontStyle: 'normal',
                                    lineHeight: '36px',
                                }}
                            >
                                Select sections to generate
                            </Typography>
                            <Typography 
                                sx={{ 
                                    fontSize: '14px',
                                    color: '#6B7280'
                                }}
                            >
                                Template: {document.templateType.template_name || 'Unknown Template'}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Select All Control */}
                    <SelectAllCheckbox
                        allSelected={selectAllState.allSelected}
                        noneSelected={selectAllState.noneSelected}
                        someSelected={selectAllState.someSelected}
                        onChange={handleSelectAllToggle}
                        label="Select all"
                    />
                </Box>

                {/* Sections List */}
                <Box sx={{ 
                    flex: 1, 
                    overflowY: 'auto',
                    mb: 4,
                    width: '100%',
                    minWidth: 'unset',
                    '@media (min-width: 769px)': {
                        minWidth: '600px',
                    }
                }}>
                    {/* {topLevelSections.map((section) => { */}
                    {sections.map((section) => {
                        // console.log("Section", section)
                        // Determine which sections have children to determine if a card or accordion is needed
                        const childSections = getChildSectionsLocal(section)
                        const hasChildren = childSections.length > 0
                        // Determine if the section is selected
                        // console.log("Selected Sections", document.selectedSections)
                        const isSelected = document.selectedSections.includes(section.section_id) || false
                        // console.log(`${section.name} is selected: ${isSelected}`)

                        // if (hasChildren) {
                        //     return (
                        //         <SectionAccordion
                        //             key={section.id}
                        //             section={section}
                        //             childSections={childSections}
                        //             selectedSections={document.selectedSections || []}
                        //             onSectionToggle={handleSectionToggle}
                        //             isExpanded={expandedAccordions[section.name] || false}
                        //             onExpandToggle={() => handleAccordionToggle(section.name)}
                        //         />
                        //     )
                        // } else {
                            return (
                                <SectionCard
                                    key={section.section_id}
                                    section={section}
                                    isSelected={isSelected}
                                    onChange={handleSectionToggle}
                                />
                            )
                        // }
                    })}
                </Box>
            </Box>
            {/* Continue Button */}
            <ContinueButton
                onClick={handleContinue}
                text="Continue"
            />
        </Box>
    )
}

export function SectionSelectionModal({
    documentId,
    open,
    onClose,
    onInsertAll,
    templateName,
    sourceDocument = null,
}) {
    const auth = useAuthInfo()
    const [sections, setSections] = useState([])
    const [error, setError] = useState(false)
    const [allSections, setAllSections] = useState([])
    const [selectedSections, setSelectedSections] = useState([])
    const [selectedSectionIds, setSelectedSectionIds] = useState([])
    const [loading, setLoading] = useState(false)
    
    console.log("Template Name", templateName)
    console.log("Source Document", sourceDocument)
    
    // Load sections and selections from localStorage or API
    useEffect(() => {
        async function loadSections() {
            if (!templateName) return;
            
            
            setLoading(true)
            try {
                // First, try to load section data from localStorage
                const cachedSectionData = sectionSelectionStorage.loadSectionData(documentId, templateName)
                
                let flatSections = []
                let needsFetch = true
                
                if (cachedSectionData && cachedSectionData.sections && cachedSectionData.sections.length > 0) {
                    console.log('Using cached section data')
                    flatSections = cachedSectionData.sections
                    needsFetch = false
                } 
                
                // If no cached data or it's stale, fetch from API
                if (needsFetch) {
                    console.log('Fetching fresh section data from API')
                    const org = auth.orgHelper.getOrgs()[0].orgId
                    const orgAccessToken = await auth.tokens.getAccessTokenForOrg(org)
                    
                    const mrt_template = await getOneMRTTemplate(templateName, orgAccessToken)
                    console.log("MRT Template", mrt_template)
                    
                    flatSections = mrt_template.sections || []
                    
                    // Save to localStorage for future use
                    sectionSelectionStorage.saveSectionData(documentId, templateName, flatSections)
                }
                
                // Set the sections
                setSections(flatSections)
                setAllSections(flatSections)
                
                // Try to load saved selections if we have a source document
                if (sourceDocument?.document_id) {
                    const savedSelectionIds = sectionSelectionStorage.loadSelections(
                        sourceDocument.document_id, 
                        templateName
                    )
                    
                    if (savedSelectionIds && savedSelectionIds.length > 0) {
                        console.log('Restoring saved selections:', savedSelectionIds)
                        // Find the section objects that match the saved IDs
                        const savedSections = flatSections.filter(section => 
                            savedSelectionIds.includes(section.section_id)
                        )
                        setSelectedSections(savedSections)
                        setSelectedSectionIds(savedSelectionIds)
                    } else {
                        // Default to all sections selected
                        setSelectedSections(flatSections)
                        const allIds = flatSections.map(s => s.section_id)
                        setSelectedSectionIds(allIds)
                    }
                } else {
                    // No source document, default to all selected
                    setSelectedSections(flatSections)
                    const allIds = flatSections.map(s => s.section_id)
                    setSelectedSectionIds(allIds)
                }
                
            } catch (error) {
                console.error("Error loading sections:", error)
                setError(true)
            } finally {
                setLoading(false)
            }
        }
        
        if (open && templateName) {
            loadSections()
        }
    }, [open, templateName, sourceDocument])

    // Update selected IDs when selections change and save to localStorage
    useEffect(() => {
        const selectedIds = selectedSections.map(section => section.section_id)
        setSelectedSectionIds(selectedIds)
        
        // Save selections to localStorage if we have a source document
        if (sourceDocument?.document_id && templateName && open) {
            sectionSelectionStorage.saveSelections(
                sourceDocument.document_id,
                templateName,
                selectedIds
            )
        }
    }, [selectedSections, sourceDocument, templateName, open])

    // Helper functions for section organization
    const getTopLevelSectionsLocal = () => {
        return getTopLevelSections(sections)
    }

    const getChildSectionsLocal = (parentSection) => {
        return getChildSections(parentSection, sections)
    }

    // Helper functions for selection state
    const getSelectAllState = () => {
        const selectedCount = selectedSections?.length || 0
        const totalCount = sections.length
        
        if (selectedCount === 0) {
            return { allSelected: false, noneSelected: true, someSelected: false }
        } else if (selectedCount === totalCount) {
            return { allSelected: true, noneSelected: false, someSelected: false }
        } else {
            return { allSelected: false, noneSelected: false, someSelected: true }
        }
    }

    const getAllDescendantsLocal = (parentSection) => {
        return getAllDescendants(parentSection, sections)
    }

    const handleSectionToggle = (section) => {
        const currentSelected = selectedSections || []
        console.log(section)
        const isCurrentlySelected = currentSelected.includes(section)
        
        let newSelected = [...currentSelected]
        
        if (isCurrentlySelected) {
            // Deselect the section and all its descendants
            newSelected = newSelected.filter(selected => selected !== section)
            const descendants = getAllDescendantsLocal(section)
            newSelected = newSelected.filter(selected => !descendants.includes(selected))
        } else {
            // Select the section and all its descendants
            if (!newSelected.includes(section)) {
                newSelected.push(section)
            }
            const descendants = getAllDescendantsLocal(section)
            descendants.forEach(descendant => {
                if (!newSelected.includes(descendant)) {
                    newSelected.push(descendant)
                }
            })
        }
        
        setSelectedSections(newSelected)
        console.log('selectedSections', newSelected)
    }

    const handleSelectAllToggle = () => {
        const { allSelected } = getSelectAllState()
        
        if (allSelected) {
            // Deselect all
            setSelectedSections([])
            console.log('selectedSections', selectedSections)
        } else {
            // Select all
            const allSections = sections.map(section => section)
            setSelectedSections(allSections)
            console.log('allSections', allSections)
        }
    }

    // const checkIfSectionHasQueryVariables = () => {
    //     const sectionsWithQueryVariables = []
    //     document.selectedSections.map((section) => {
    //         console.log('section', section) // TODO: remove this
    //         if (section.fixed_elements) {
    //             section.fixed_elements.map((element) => {
    //                 let query_sql = element.query.query_sql
    //                 if (query_sql) {
    //                     for (const regex of placeholder_regexps) {
    //                         if (query_sql.match(regex)) {
    //                             sectionsWithQueryVariables.push(section)
    //                             break
    //                         }
    //                     }
    //                 }
    //             })
    //         }
    //     })
    //     document.sectionsWithQueryVariables = sectionsWithQueryVariables
    //     return document.sectionsWithQueryVariables
    // }

    const handleContinue = () => {
        // const sectionsWithQueryVariables = checkIfSectionHasQueryVariables()
        // console.log('sectionsWithQueryVariables', sectionsWithQueryVariables)
        // if (sectionsWithQueryVariables) {
        //     const newSteps = [...steps]
        //     newSteps.splice(2, 0, 'Database Query')
        //     setSteps(newSteps)
        //     console.log('steps', steps)
        //     onNextClick()
        // } else {
        //     onNextClick()
        // }
        onClose()
    }

    // Get top level sections to determin how many cards/accordions are needed
    const topLevelSections = getTopLevelSectionsLocal()
    // Get the select all state to determine the select all checkbox state
    const selectAllState = getSelectAllState()

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="section-selection-modal-title"
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '90%',
                    maxWidth: '1200px',
                    height: '80%',
                    minHeight: '600px',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 3,
                    backgroundColor: '#F9FAFB',
                }}
            >
            {loading ? (
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    minHeight: '300px',
                    gap: 2
                }}>
                    <GradientCircularProgressIndicator size={100} />
                    <Typography variant="body2" sx={{ color: '#6B7280' }}>
                        Loading sections...
                    </Typography>
                </Box>
            ) : error ? (
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    minHeight: '400px',
                    width: '100%' 
                }}>
                    <Alert severity="error">
                        Error loading sections. Please try again.
                    </Alert>
                </Box>
            ) : (
                <>
                    {/* Header */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        width: '100%',
                        mb: 2,
                    }}>
                        <IconButton
                            onClick={onClose}
                            sx={{
                                width: '30px',
                                height: '30px',
                                ml: '-7px',
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                }
                            }}
                        >
                            <CloseIcon sx={{ width: '16px', height: '16px', color: '#111827' }} />
                        </IconButton>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'flex-start',
                            width: '100%',
                        }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: 1,
                                }}
                            >
                                <Typography 
                                    variant="h6" 
                                    id="section-selection-modal-title"
                                    sx={{ 
                                        fontSize: '24px', 
                                        fontWeight: 700, 
                                        lineHeight: '36px'
                                    }}
                                >
                                    Select sections to regenerate
                                </Typography>
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        color: '#6B7280', 
                                        lineHeight: '20px', 
                                        fontSize: '14px' 
                                    }}
                                >
                                    Choose which sections should be regenerated with the updated source document
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                {/* Debug Helper Buttons - Remove these in production */}
                                {process.env.NODE_ENV === 'development' && false && (
                                    <>
                                        <Button
                                            size="small"
                                            onClick={() => {
                                                console.log('=== SECTION SELECTION LOCAL STORAGE DEBUG ===')
                                                const appKeys = []
                                                for (let i = 0; i < localStorage.length; i++) {
                                                    const key = localStorage.key(i)
                                                    // Filter for section-related keys
                                                    if (key && (
                                                        key.includes('sectionSelections') ||
                                                        key.includes('sectionData') ||
                                                        key.includes('documentStatuses') ||
                                                        key.includes('activePolls')
                                                    )) {
                                                        appKeys.push(key)
                                                        const value = localStorage.getItem(key)
                                                        try {
                                                            const parsed = JSON.parse(value)
                                                            console.log(`📦 ${key}:`, parsed)
                                                        } catch {
                                                            console.log(`📦 ${key}:`, value)
                                                        }
                                                    }
                                                }
                                                console.log(`Found ${appKeys.length} section-related localStorage entries`)
                                                console.log('Keys:', appKeys)
                                                
                                                // Also log current state
                                                console.log('=== CURRENT MODAL STATE ===')
                                                console.log('Template Name:', templateName)
                                                console.log('Source Document:', sourceDocument)
                                                console.log('Selected Sections:', selectedSections.length)
                                                console.log('Selected Section IDs:', selectedSectionIds)
                                                console.log('======================================')
                                            }}
                                            sx={{ 
                                                textTransform: 'none',
                                                p: '0px',
                                                borderRadius: '6px',
                                                boxShadow: 'inset 0px 0px 0px 1px #10B981',
                                                backgroundColor: '#ECFDF5',
                                                '&:hover': {
                                                    backgroundColor: '#D1FAE5',
                                                },
                                                minWidth: 'auto'
                                            }}
                                        >
                                            <Typography sx={{ 
                                                lineHeight: '16px', 
                                                fontSize: '12px', 
                                                fontWeight: 'medium', 
                                                color: '#047857', 
                                                py: '6px', 
                                                px: '10px' 
                                            }}>
                                                🔍 Log Storage
                                            </Typography>
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => {
                                                if (window.confirm('Are you sure you want to clear all section-related localStorage? This cannot be undone.')) {
                                                    let clearedCount = 0
                                                    const keysToRemove = []
                                                    
                                                    // Collect all section-related keys
                                                    for (let i = 0; i < localStorage.length; i++) {
                                                        const key = localStorage.key(i)
                                                        if (key && (
                                                            key.includes('sectionSelections') ||
                                                            key.includes('sectionData')
                                                        )) {
                                                            keysToRemove.push(key)
                                                        }
                                                    }
                                                    
                                                    // Remove collected keys
                                                    keysToRemove.forEach(key => {
                                                        localStorage.removeItem(key)
                                                        clearedCount++
                                                    })
                                                    
                                                    console.log(`✅ Cleared ${clearedCount} section-related localStorage entries`)
                                                    console.log('Removed keys:', keysToRemove)
                                                    
                                                    // Clear current selections and reload sections
                                                    setSelectedSections([])
                                                    setSelectedSectionIds([])
                                                    
                                                    // Optionally reload the modal to reflect cleared state
                                                    window.location.reload()
                                                }
                                            }}
                                            sx={{ 
                                                textTransform: 'none',
                                                p: '0px',
                                                borderRadius: '6px',
                                                boxShadow: 'inset 0px 0px 0px 1px #EF4444',
                                                backgroundColor: '#FEE2E2',
                                                '&:hover': {
                                                    backgroundColor: '#FECACA',
                                                },
                                                minWidth: 'auto'
                                            }}
                                        >
                                            <Typography sx={{ 
                                                lineHeight: '16px', 
                                                fontSize: '12px', 
                                                fontWeight: 'medium', 
                                                color: '#B91C1C', 
                                                py: '6px', 
                                                px: '10px' 
                                            }}>
                                                🗑️ Clear Storage
                                            </Typography>
                                        </Button>
                                    </>
                                )}
                            </Box>
                        </Box>
                        
                        {/* Select All Control */}
                        <Box sx={{ mt: 2, width: '100%' }}>
                            <SelectAllCheckbox
                                allSelected={selectAllState.allSelected}
                                noneSelected={selectAllState.noneSelected}
                                someSelected={selectAllState.someSelected}
                                onChange={handleSelectAllToggle}
                                label="Select all"
                            />
                        </Box>
                    </Box>

                    {/* Sections List */}
                    <Box sx={{ 
                        flex: 1, 
                        overflowY: 'auto',
                        mb: 2,
                        pr: 1,
                        '&::-webkit-scrollbar': {
                            width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: '#888',
                            borderRadius: '4px',
                            '&:hover': {
                                background: '#555',
                            }
                        }
                    }}>
                        {sections.map((section, index) => {
                            const isSelected = selectedSections.includes(section) || false
                            return (
                                <SectionCard
                                    key={section.section_id || index}
                                    section={section}
                                    isSelected={isSelected}
                                    onChange={handleSectionToggle}
                                    lastSection={index === sections.length - 1}
                                />
                            )
                        })}
                    </Box>
                    
                    {/* Footer with Continue Button */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        pt: 2,
                        // borderTop: '1px solid #E5E7EB'
                    }}>
                        <ContinueButton
                            disabled={selectedSectionIds.length === 0}
                            text="Regenerate"
                            onClick={() => {
                                console.log('selectedSectionIds', selectedSectionIds)
                                // Call the parent's onInsertAll which will handle the API call
                                // The parent should clear localStorage on successful 202 response
                                onInsertAll(selectedSectionIds)
                            }}
                            bold={true}
                        />
                    </Box>
                </>
            )}
            </Box>
        </Modal>
    )
}

export default SelectDocumentSectionToBeGenerated
